import React, { useEffect, useMemo, useRef, useState } from "react";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import axios from "axios";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import AppButton from "../shared/AppButton";
import AppTable from "../shared/AppTable";
import CustomFilterInput from "../shared/CustomFilterInput";
import { API_BASE_URL } from "../config/apiConfig";

const getAuthHeaders = () => {
  const token = localStorage.getItem("token")?.replace(/"/g, "");
  return { Authorization: `Bearer ${token}` };
};

const downloadBlob = (blob, fileName) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", fileName);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const getResponseFileName = (headers, fallbackName) => {
  const disposition = headers?.["content-disposition"];
  const fileNameMatch = disposition?.match(/filename="?([^"]+)"?/i);
  return fileNameMatch?.[1] || fallbackName;
};

const hasPendingReports = (rows = []) =>
  rows.some((row) => {
    const status = String(row?.status || "").toLowerCase();
    return !["completed", "failed", "error"].includes(status);
  });

const GeneratedReportPanel = ({
  reports = [],
  initialReportId,
  activeReportId: controlledActiveReportId,
  onActiveReportChange,
  showTabs = true,
}) => {
  const { t } = useTranslation();
  const [internalActiveReportId, setInternalActiveReportId] = useState(
    initialReportId || reports[0]?.id,
  );
  const [filtersByReport, setFiltersByReport] = useState(() =>
    reports.reduce(
      (acc, report) => ({
        ...acc,
        [report.id]: report.defaultFilters || {},
      }),
      {},
    ),
  );
  const [reportLists, setReportLists] = useState({});
  const [listLoading, setListLoading] = useState({});
  const [generateLoading, setGenerateLoading] = useState({});
  const [downloadLoading, setDownloadLoading] = useState({});
  const listLoadingRef = useRef({});

  const activeReportId = controlledActiveReportId || internalActiveReportId;
  const setActiveReportId = (reportId) => {
    if (onActiveReportChange) {
      onActiveReportChange(reportId);
    } else {
      setInternalActiveReportId(reportId);
    }
  };

  const activeReport = useMemo(
    () => reports.find((report) => report.id === activeReportId),
    [activeReportId, reports],
  );

  const payersData = localStorage.getItem("payersData");
  const payerDataOptions = payersData ? JSON.parse(payersData) : [];

  const handleFilterChange = (reportId, e) => {
    const { name, value } = e.target;
    setFiltersByReport((prev) => ({
      ...prev,
      [reportId]: {
        ...prev[reportId],
        [name]: value,
      },
    }));
  };

  const validateFilters = (report) => {
    const filters = filtersByReport[report.id] || {};

    if (report.fileOnly && !filters.id) return t("Report ID is required");
    if (report.fields?.includes("dateRange")) {
      if (!filters.from_date || !filters.to_date) {
        return t("From Date and To Date are required");
      }
    }
    if (report.fields?.includes("payer") && !filters.payer_id) {
      return t("Payer is required");
    }
    if (report.fields?.includes("batch") && !filters.batch_id) {
      return t("Batch ID is required");
    }
    if (report.fields?.includes("format") && !filters.format) {
      return t("Format is required");
    }

    return "";
  };

  const fetchReportList = async (report = activeReport) => {
    if (!report?.list) return;
    if (listLoadingRef.current[report.id]) return;

    try {
      listLoadingRef.current[report.id] = true;
      setListLoading((prev) => ({ ...prev, [report.id]: true }));
      const response = await axios.get(`${API_BASE_URL}${report.list}`, {
        headers: getAuthHeaders(),
      });
      setReportLists((prev) => ({
        ...prev,
        [report.id]: response?.data?.data || [],
      }));
    } catch (error) {
      console.error(error);
      toast.error(t("Error fetching report list"));
    } finally {
      listLoadingRef.current[report.id] = false;
      setListLoading((prev) => ({ ...prev, [report.id]: false }));
    }
  };

  const handleGenerate = async (report) => {
    const validationMessage = validateFilters(report);
    if (validationMessage) {
      toast.error(validationMessage);
      return;
    }

    try {
      setGenerateLoading((prev) => ({ ...prev, [report.id]: true }));
      await axios.post(
        `${API_BASE_URL}${report.generate}`,
        filtersByReport[report.id],
        { headers: getAuthHeaders() },
      );
      toast.success(t("Report generation started"));
      fetchReportList(report);
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.error || t("Error generating report"));
    } finally {
      setGenerateLoading((prev) => ({ ...prev, [report.id]: false }));
    }
  };

  const handleDownload = async (report, row) => {
    const reportId = row?.id || filtersByReport[report.id]?.id;
    if (!reportId) {
      toast.error(t("Report ID is required"));
      return;
    }

    try {
      setDownloadLoading((prev) => ({
        ...prev,
        [`${report.id}-${reportId}`]: true,
      }));
      const response = await axios.get(`${API_BASE_URL}${report.file}`, {
        params: { id: reportId },
        responseType: "blob",
        headers: getAuthHeaders(),
      });
      const fallbackExtension = row?.format === "pdf" ? "pdf" : "xlsx";
      const fallbackName = `${report.id}-${reportId}.${fallbackExtension}`;
      downloadBlob(
        response.data,
        getResponseFileName(response.headers, fallbackName),
      );
    } catch (error) {
      console.error(error);
      toast.error(t("Error downloading report file"));
    } finally {
      setDownloadLoading((prev) => ({
        ...prev,
        [`${report.id}-${reportId}`]: false,
      }));
    }
  };

  useEffect(() => {
    if (activeReport?.list && !reportLists[activeReport.id]) {
      fetchReportList(activeReport);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeReportId]);

  useEffect(() => {
    if (!activeReport?.list) return undefined;

    const rows = reportLists[activeReport.id] || [];
    if (!rows.length || !hasPendingReports(rows)) return undefined;

    const intervalId = setInterval(() => {
      fetchReportList(activeReport);
    }, 10000);

    return () => clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeReportId, reportLists]);

  const renderPayerOptions = () => (
    <>
      <option value="">{t("Select Payer")}</option>
      {payerDataOptions?.map((category) => (
        <optgroup
          key={category.id}
          label={
            category?.name_en
              ? `${category.name_en} (${category.license_id})`
              : ""
          }
        >
          {category.payers.map((insurance) => (
            <option key={insurance.id} value={insurance.id}>
              {insurance.name_en} ({insurance?.license_id})
            </option>
          ))}
        </optgroup>
      ))}
    </>
  );

  const renderStatusBadge = (status) => {
    const value = status || "pending";
    const lowerValue = value.toLowerCase();
    let theme = "bg-slate-100 text-slate-700";

    if (lowerValue === "completed") theme = "bg-emerald-100 text-emerald-700";
    else if (lowerValue.includes("fail") || lowerValue.includes("error"))
      theme = "bg-red-100 text-red-700";
    else if (lowerValue.includes("process") || lowerValue.includes("pending"))
      theme = "bg-amber-100 text-amber-700";

    return (
      <span
        className={`inline-flex px-2 py-0.5 rounded-md text-[10px] font-bold uppercase ${theme}`}
      >
        {value}
      </span>
    );
  };

  const getColumns = (report) => [
    {
      header: t("File"),
      render: (row) => {
        const isCompleted = String(row.status || "").toLowerCase() === "completed";
        const loadingKey = `${report.id}-${row.id}`;

        return (
          <button
            type="button"
            title={t("Download")}
            disabled={!isCompleted || Boolean(downloadLoading[loadingKey])}
            onClick={() => handleDownload(report, row)}
            className={`flex h-[22px] w-[22px] items-center justify-center transition-colors ${
              isCompleted
                ? "cursor-pointer text-gray-500 hover:text-blue-600"
                : "cursor-default text-gray-300"
            }`}
          >
            <FileDownloadOutlinedIcon sx={{ fontSize: 19 }} />
          </button>
        );
      },
    },
    { header: t("ID"), render: (row) => row.id || "-" },
    { header: t("Status"), render: (row) => renderStatusBadge(row.status) },
    {
      header: t("Format"),
      render: (row) => (
        <span className="uppercase text-[11px] font-semibold">
          {row.format || "-"}
        </span>
      ),
    },
    {
      header: t("Date Range"),
      render: (row) => {
        if (!row.from_date && !row.to_date) return "-";
        return `${row.from_date || "-"} / ${row.to_date || "-"}`;
      },
    },
    { header: t("Payer"), render: (row) => row.payer_name || row.payer_id || "-" },
    { header: t("Batch ID"), render: (row) => row.batch_id || "-" },
    {
      header: t("Ordered"),
      render: (row) => row.date_ordered || row.created_at || "-",
    },
    {
      header: t("Error"),
      render: (row) => (
        <span className="inline-block max-w-[240px] truncate text-red-600">
          {row.error || "-"}
        </span>
      ),
    },
  ];

  const renderFilters = (report) => {
    const filters = filtersByReport[report.id] || {};

    return (
      <div className="bg-white border border-slate-200 rounded-lg p-2 mb-2">
        <div className="flex flex-wrap items-end gap-1.5">
          {report.fields?.includes("dateRange") && (
            <>
              <div className="w-full sm:w-[150px]">
                <CustomFilterInput
                  label={t("From Date")}
                  name="from_date"
                  type="date"
                  value={filters.from_date || ""}
                  onChange={(e) => handleFilterChange(report.id, e)}
                />
              </div>
              <div className="w-full sm:w-[150px]">
                <CustomFilterInput
                  label={t("To Date")}
                  name="to_date"
                  type="date"
                  value={filters.to_date || ""}
                  onChange={(e) => handleFilterChange(report.id, e)}
                />
              </div>
            </>
          )}

          {report.fields?.includes("payer") && (
            <div className="w-full sm:w-[260px]">
              <CustomFilterInput
                label={t("Payer")}
                name="payer_id"
                isSelect
                value={filters.payer_id || ""}
                onChange={(e) => handleFilterChange(report.id, e)}
                placeholder={t("Select Payer")}
                renderOptions={renderPayerOptions}
              />
            </div>
          )}

          {report.fields?.includes("batch") && (
            <div className="w-full sm:w-[180px]">
              <CustomFilterInput
                label={t("Batch ID")}
                name="batch_id"
                value={filters.batch_id || ""}
                onChange={(e) => handleFilterChange(report.id, e)}
                placeholder={t("Batch ID")}
              />
            </div>
          )}

          {report.fields?.includes("reportId") && (
            <div className="w-full sm:w-[160px]">
              <CustomFilterInput
                label={t("Report ID")}
                name="id"
                value={filters.id || ""}
                onChange={(e) => handleFilterChange(report.id, e)}
                placeholder={t("Report ID")}
              />
            </div>
          )}

          {report.fields?.includes("format") && (
            <div className="w-full sm:w-[120px]">
              <CustomFilterInput
                label={t("Format")}
                name="format"
                isSelect
                value={filters.format || "excel"}
                onChange={(e) => handleFilterChange(report.id, e)}
                renderOptions={() => (
                  <>
                    <option value="excel">{t("Excel")}</option>
                    <option value="pdf">{t("PDF")}</option>
                  </>
                )}
              />
            </div>
          )}

          <div className="flex gap-1.5">
            {report.fileOnly ? (
              <AppButton
                label={t("Download")}
                onClick={() => handleDownload(report)}
                isLoading={Boolean(downloadLoading[`${report.id}-${filters.id}`])}
                startIcon={
                  <FileDownloadOutlinedIcon className="!text-[13px]" />
                }
              />
            ) : (
              <>
                <AppButton
                  label={t("Generate")}
                  onClick={() => handleGenerate(report)}
                  isLoading={Boolean(generateLoading[report.id])}
                  startIcon={<DoneAllIcon className="!text-[12px]" />}
                />
                <AppButton
                  label={t("Refresh")}
                  variant="outlined"
                  onClick={() => fetchReportList(report)}
                  isLoading={Boolean(listLoading[report.id])}
                  startIcon={<RefreshIcon className="!text-[13px]" />}
                />
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  if (!activeReport) return null;

  return (
    <div className="mt-2">
      {showTabs && (
        <div className="bg-white border border-slate-200 rounded-lg p-1.5 mb-2">
          <div className="flex flex-wrap gap-1">
            {reports.map((report) => {
              const isActive = activeReportId === report.id;
              return (
                <button
                  key={report.id}
                  type="button"
                  title={t(report.description || report.label)}
                  onClick={() => setActiveReportId(report.id)}
                  className={`h-[26px] px-2 rounded-[4px] text-[11px] font-semibold border transition-colors ${
                    isActive
                      ? "bg-primary text-white border-primary"
                      : "bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100"
                  }`}
                >
                  {t(report.label)}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {renderFilters(activeReport)}

      {!activeReport.fileOnly && (
        <AppTable
          columns={getColumns(activeReport)}
          data={reportLists[activeReport.id] || []}
          loading={Boolean(listLoading[activeReport.id])}
          actions={false}
          bodyRowPadding="3px"
        />
      )}
    </div>
  );
};

export default GeneratedReportPanel;
